import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { configuracionApi } from '@/api/configuracion'
import type { CredencialDemo, Emisor } from '@/types/api'

/**
 * Valores públicos de configuración (los que el POS y el molino necesitan).
 * Los defaults replican los del backend para que la app funcione igual
 * mientras llega la respuesta o si la petición falla.
 */
const DEFAULTS: Record<string, unknown> = {
  nombre_negocio: 'Café POS',
  logo_url: '',
  comprobante_pie: '',
  umbral_confirmacion_venta: 50_000,
  anulacion_habilitada: false,
  anulacion_minutos_cajero: 15,
  anulacion_solo_admin: false,
  pos_meta_dia: true,
  molino_umbral_desviacion_pct: 5,
  facturacion_electronica_habilitada: false,
  impuesto_por_defecto: 'ninguno',
}

export const useConfiguracionStore = defineStore('configuracion', () => {
  const valores = ref<Record<string, unknown>>({ ...DEFAULTS })
  const cargada = ref(false)
  // Llegan con la marca (sin sesión): la demo pública lo avisa en el login y en el shell.
  const marcaCargada = ref(false)
  const demo = ref(false)
  const credencialesDemo = ref<CredencialDemo[]>([])

  function num(clave: string): number {
    const v = valores.value[clave]
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : Number(DEFAULTS[clave] ?? 0)
  }

  const nombreNegocio = computed(() => {
    const v = valores.value.nombre_negocio
    return typeof v === 'string' && v.trim() ? v : 'Café POS'
  })
  const logoUrl = computed(() => {
    const v = valores.value.logo_url
    return typeof v === 'string' && v.trim() ? v : null
  })
  const comprobantePie = computed(() => {
    const v = valores.value.comprobante_pie
    return typeof v === 'string' ? v.trim() : ''
  })
  const umbralConfirmacion = computed(() => num('umbral_confirmacion_venta'))
  const anulacionHabilitada = computed(() => valores.value.anulacion_habilitada === true)
  const anulacionMinutos = computed(() => num('anulacion_minutos_cajero'))
  const anulacionSoloAdmin = computed(() => valores.value.anulacion_solo_admin === true)
  const metaDiaPos = computed(() => valores.value.pos_meta_dia !== false)
  const molinoUmbral = computed(() => num('molino_umbral_desviacion_pct'))
  // Facturación electrónica: con el interruptor apagado el POS no pide cliente
  // y el comprobante no se presenta como documento equivalente.
  const facturacionHabilitada = computed(
    () => valores.value.facturacion_electronica_habilitada === true && !demo.value,
  )
  const impuestoPorDefecto = computed(() => {
    const v = valores.value.impuesto_por_defecto
    return typeof v === 'string' && v ? v : 'ninguno'
  })
  /** Datos del emisor, para imprimir sin conexión el documento de contingencia. */
  const emisor = computed<Emisor>(() => {
    const txt = (k: string) => {
      const v = valores.value[k]
      return typeof v === 'string' ? v.trim() : ''
    }
    const [nit, dv] = txt('fiscal_nit').split('-')
    return {
      nit: nit ?? '',
      dv: dv ?? null,
      razon_social: txt('fiscal_razon_social'),
      nombre_comercial: txt('fiscal_nombre_comercial') || nombreNegocio.value,
      direccion: txt('fiscal_direccion'),
      municipio: txt('fiscal_municipio'),
      telefono: txt('fiscal_telefono'),
      correo: txt('fiscal_correo'),
      regimen: txt('fiscal_regimen'),
      responsable_iva: valores.value.fiscal_responsable_iva === true,
      responsable_inc: valores.value.fiscal_responsable_inc === true,
    }
  })

  async function cargar(forzar = false) {
    if (cargada.value && !forzar) return
    try {
      const { valores: v } = await configuracionApi.publica()
      aplicar(v)
      cargada.value = true
    } catch {
      // Sin red o sin sesión: se queda con los defaults.
    }
  }

  /** Aplica valores llegados por WebSocket (`config.updated`) o tras guardar. */
  function aplicar(v: Record<string, unknown>) {
    valores.value = { ...DEFAULTS, ...v }
  }

  /** Nombre, logo y modo demo, sin sesión: para el login. No pisa lo ya cargado. */
  async function cargarMarca() {
    try {
      const m = await configuracionApi.marca()
      valores.value = { ...valores.value, nombre_negocio: m.nombre_negocio, logo_url: m.logo_url ?? '' }
      demo.value = m.demo === true
      credencialesDemo.value = m.demo_credenciales ?? []
      marcaCargada.value = true
    } catch {
      /* sin red: defaults */
    }
  }

  return {
    valores,
    cargada,
    marcaCargada,
    demo,
    credencialesDemo,
    nombreNegocio,
    logoUrl,
    comprobantePie,
    umbralConfirmacion,
    anulacionHabilitada,
    anulacionMinutos,
    anulacionSoloAdmin,
    metaDiaPos,
    molinoUmbral,
    facturacionHabilitada,
    impuestoPorDefecto,
    emisor,
    cargar,
    cargarMarca,
    aplicar,
  }
})
